import { ApolloClient, InMemoryCache, defaultDataIdFromObject, createHttpLink } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { supabase } from '../lib/supabase';

// Create HTTP link
const httpLink = createHttpLink({
  uri: process.env.REACT_APP_GRAPHQL_URL
});

// Create auth link that adds JWT token to headers
const authLink = setContext(async (_, { headers }) => {
  try {
    // Get the current session from Supabase
    const { data: { session } } = await supabase.auth.getSession();
    
    // Return the headers to the context so httpLink can read them
    return {
      headers: {
        ...headers,
        // Add the JWT token if available
        ...(session?.access_token && {
          authorization: `Bearer ${session.access_token}`,
        }),
      }
    };
  } catch (error) {
    console.error('❌ Error getting auth token:', error);
    return { headers };
  }
});

// Create error link to handle authentication errors
const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
  console.log('🚨 Apollo GraphQL Error:', {
    operation: operation.operationName,
    variables: operation.variables,
    graphQLErrors,
    networkError
  });

  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, locations, path }) => {
      console.error(`❌ GraphQL error: ${message}`, { locations, path });
    });
  }

  if (networkError) {
    console.error('🌐 Network error:', networkError);
  }
});

// Create Apollo Client instance with HTTP-only link
const client = new ApolloClient({
  link: errorLink.concat(authLink).concat(httpLink),
  cache: new InMemoryCache({
    // Customize the cache ID function to ensure proper caching
    dataIdFromObject: (object) => {
      // Use token ID for Position objects to ensure proper updates
      if (object.__typename === 'Position' && object.id) {
        return `Position:${object.id}`;
      }
      // Fall back to default behavior for other types
      return defaultDataIdFromObject(object);
    },
  }),
  defaultOptions: {
    watchQuery: {
      fetchPolicy: 'cache-and-network',
      errorPolicy: 'all',
    },
    query: {
      fetchPolicy: 'cache-first',
      errorPolicy: 'all',
    },
    mutate: {
      errorPolicy: 'all',
    },
  },
});

// Export a function to clear cache on logout
export const clearApolloCache = () => {
  client.clearStore();
};

export default client;
